import React, { useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * Input · Select · Textarea — ui-v2.5. Campos con envoltorio compartido (etiqueta, ayuda, error).
 * Especificación: catálogo · sección Input · Select · Textarea.
 * NO son los homónimos de ui-v2 (aquellos son shadcn sin envoltorio); estos viven en ui-v2.5/.
 *
 * Estados: normal · foco (border-v25-focus + shadow-v25-focus) · error (rojo) · deshabilitado ·
 * prellenado (tokens v25-prefill-*) · con icono interno · monoespaciado.
 *
 * <Input label="Nombre *" hint="…" />
 * <Input label="Email *" icon={Mail} />
 * <Input label="DNI *" error="Formato no válido." />
 * <Input label="Código" value="LH-0042" disabled hint="Lo asigna RRHH." />
 * <Input label="Prellenado" value="Ana García" prefilled hint="Dato aportado por la persona." />
 * <Input label="IBAN" value="ES91…" mono />
 * <Select label="Empresa *"><option>…</option></Select>
 * <Textarea label="Observaciones" rows={2} />
 */

// Base SIN padding horizontal: se aplica por rama para que twMerge no lo desdedupe
// (mismo motivo documentado en ui-v2/Input.jsx: px-3 + pl-9 no se resuelven de forma estable).
const controlBase =
  "w-full rounded-v25-md border bg-white py-2 text-sm text-v25-ink-900 outline-none transition-[border-color,box-shadow] " +
  "placeholder:text-v25-ink-400 disabled:cursor-not-allowed disabled:bg-v25-surface-sunken disabled:text-v25-ink-400";

function stateClasses({ error, prefilled }) {
  if (error) return "border-red-600 focus:border-red-600 focus:shadow-v25-focus-error";
  if (prefilled) return "border-v25-prefill-bd bg-v25-prefill-bg focus:border-v25-focus focus:shadow-v25-focus";
  return "border-v25-line-strong focus:border-v25-focus focus:shadow-v25-focus";
}

/**
 * Envoltorio compartido: etiqueta arriba, ayuda/error debajo.
 *
 * `htmlFor` ATA LA ETIQUETA AL CONTROL. Hasta la Fase 11 la `<label>` no lo llevaba y el control
 * no tenía `id`, así que la etiqueta era puramente visual: un lector de pantalla llegaba al campo
 * y **no sabía decir cómo se llama**. Afectaba a los tres controles del DS y, con ellos, a las
 * diez pantallas que los usan. Es la misma clase de defecto que el botón de icono sin
 * `aria-label`, y se arregla igual: dando nombre a lo que ya lo tenía escrito al lado.
 *
 * El id se genera con `useId` —el mismo mecanismo que ya usa Tooltip— salvo que quien llama pase
 * el suyo. Cero cambio visual.
 */
function Field({ id, label, hint, error, prefilled, className, children }) {
  return (
    <div className={className}>
      {label && <label htmlFor={id} className="mb-1 block text-xs font-medium text-v25-ink-700">{label}</label>}
      {children}
      {error ? (
        <p className="mt-1 text-[11px] text-red-600">{error}</p>
      ) : hint ? (
        <p className={cn("mt-1 text-[11px]", prefilled ? "text-v25-prefill-fg" : "text-v25-ink-400")}>{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = React.forwardRef(
  ({ label, hint, error, icon: Icon, prefilled, mono, className, wrapperClassName, id, ...props }, ref) => {
    const generado = useId();
    const idCampo = id || generado;
    const control = (
      <div className="relative">
        {Icon && <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v25-ink-400" />}
        <input
          ref={ref}
          id={idCampo}
          className={cn(controlBase, stateClasses({ error, prefilled }), Icon ? "pl-9 pr-3" : "px-3", mono && "font-mono text-[13px]", className)}
          {...props}
        />
      </div>
    );
    return <Field id={idCampo} label={label} hint={hint} error={error} prefilled={prefilled} className={wrapperClassName}>{control}</Field>;
  }
);
Input.displayName = "Input";

export const Select = React.forwardRef(
  ({ label, hint, error, prefilled, className, wrapperClassName, children, id, ...props }, ref) => {
    const generado = useId();
    const idCampo = id || generado;
    const control = (
      <div className="relative">
        <select
          ref={ref}
          id={idCampo}
          className={cn(controlBase, stateClasses({ error, prefilled }), "cursor-pointer appearance-none pl-3 pr-9", className)}
          {...props}
        >
          {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v25-ink-400" />
      </div>
    );
    return <Field id={idCampo} label={label} hint={hint} error={error} prefilled={prefilled} className={wrapperClassName}>{control}</Field>;
  }
);
Select.displayName = "Select";

export const Textarea = React.forwardRef(
  ({ label, hint, error, prefilled, rows = 3, className, wrapperClassName, id, ...props }, ref) => {
    const generado = useId();
    const idCampo = id || generado;
    const control = (
      <textarea
        ref={ref}
        id={idCampo}
        rows={rows}
        className={cn(controlBase, stateClasses({ error, prefilled }), "resize-y px-3", className)}
        {...props}
      />
    );
    return <Field id={idCampo} label={label} hint={hint} error={error} prefilled={prefilled} className={wrapperClassName}>{control}</Field>;
  }
);
Textarea.displayName = "Textarea";
